import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { WorkflowFolderCreateModal } from "@/pages/workflows/edit/components/WorkflowFolderCreateModal.tsx";
import { WorkflowEditSidebar } from "@/pages/workflows/edit/components/WorkflowEditSidebar.tsx";
import {
  useWorkflowEditPage,
  WorkflowEditPageProps,
  WorkflowEditPageStep,
} from "@/pages/workflows/edit/pages/workflow-edit-pages.tsx";
import { WorkflowTitleSlot } from "@/pages/workflows/edit/pages/WorkflowTitleSlot.tsx";
import { buildWorkflowEditDetailsSchema } from "@/pages/workflows/edit/pages/workflow-edit-details.schema.ts";
import { useWorkflowFolders } from "@/queries/workflows/workflow-folder.query.ts";
import { useWorkflowUpdateDetails } from "@/queries/workflows/workflow.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { ApiWorkflowUpdateDetails } from "@/types/workflows/workflow.types.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  FullScreenPage,
  Input,
  Select,
  Stack,
  Textarea,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

export const WorkflowEditDetailsPage = ({ workflow, workflowVersion }: WorkflowEditPageProps) => {
  const { t } = useTranslation();
  const { confirmExit, navigateToStep } = useWorkflowEditPage();
  const { canIEdit } = usePermissions();
  const { openModal } = useModals();

  useTitle(t("workflow:configure_workflow"), t("workflow:workflows"));

  const workflowUpdateDetails = useWorkflowUpdateDetails();
  const { data: workflowFolders, isLoading: isWorkflowFoldersLoading } = useWorkflowFolders();
  const workflowFolderOptions = useMemo(() => workflowFolders ?? [], [workflowFolders]);
  const canCreateWorkflowFolders = canIEdit(ApiPermission.workflows_manage_all);

  const workflowEditDetailsSchema = buildWorkflowEditDetailsSchema();

  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors, isDirty },
  } = useForm<Pick<ApiWorkflowUpdateDetails, "title" | "description" | "folder_id">>({
    mode: "onSubmit",
    resolver: yupResolver(workflowEditDetailsSchema),
    values: {
      title: workflow.title,
      description: workflow.description,
      folder_id: workflow.folder?.id ?? undefined,
    },
  });

  const handleSave = handleSubmit(async (data) => {
    if (!isDirty) {
      navigateToStep(workflow.id, WorkflowEditPageStep.visibility);
      return;
    }
    try {
      await workflowUpdateDetails.mutateAsync({
        workflowId: workflow.id,
        workflowVersionId: workflow.current_version_id,
        payload: {
          ...data,
          emoji: workflow.emoji,
          public_title: workflow.public_title,
          public_description: workflow.public_description,
          is_shared: workflow.is_shared,
        },
      });

      analyticsService.trackEvent(analyticEvents.workflows.edited, {
        [analyticProperties.id]: workflow.id,
        [analyticProperties.tab]: WorkflowEditPageStep.details,
      });

      navigateToStep(workflow.id, WorkflowEditPageStep.visibility);
    } catch {
      //  handle error
    }
  });

  return (
    <FullScreenPage
      isCenterVertical={false}
      titleSlot={
        <WorkflowTitleSlot
          step={WorkflowEditPageStep.details}
          workflow={workflow}
          workflowVersion={workflowVersion}
        />
      }
      backSlot={
        <Button variant={"secondary"} size={"lg"} onClick={() => confirmExit()}>
          {t("common:exit")}
        </Button>
      }
      actionsSlot={
        <Button size={"lg"} onClick={handleSave} isLoading={workflowUpdateDetails.isPending}>
          {t("common:continue")}
        </Button>
      }
      sidebarSlot={
        <WorkflowEditSidebar
          step={WorkflowEditPageStep.details}
          workflow={workflow}
          workflowVersion={workflowVersion}
        />
      }
    >
      <form onSubmit={handleSave}>
        <Stack gap={"sm"} justify={"center"}>
          <Stack className={"card-panel"} gap={"2xl"}>
            <Stack gap={"lg"}>
              <Input
                type={"text"}
                label={t("common:title")}
                size={"lg"}
                required={true}
                {...register("title")}
                error={errors.title?.message}
              />
              <Textarea
                label={t("common:description")}
                {...register("description")}
                error={errors.description?.message}
              />
            </Stack>
          </Stack>
          <Stack className={"card-panel"} gap={"lg"}>
            <Title header={t("workflow:category")} size={"md"} />
            <Controller
              render={({ field: { value, onChange } }) => (
                <Select
                  label={t("workflow:category")}
                  emptyTitle={t("common:select")}
                  value={value}
                  required={true}
                  options={workflowFolderOptions}
                  onChange={(nextValue) => {
                    onChange(nextValue ?? null);
                  }}
                  error={errors.folder_id?.message}
                  isLoading={isWorkflowFoldersLoading}
                  searchable={true}
                  onAddNew={
                    canCreateWorkflowFolders
                      ? (searchText) => {
                          openModal(WorkflowFolderCreateModal, {
                            initialTitle: searchText,
                            onSaved: async (folder) => {
                              setValue("folder_id", folder.id, {
                                shouldDirty: true,
                                shouldValidate: true,
                              });
                            },
                          });
                        }
                      : undefined
                  }
                />
              )}
              control={control}
              name={"folder_id"}
            />
          </Stack>

          <Button type={"submit"} size={"lg"} isLoading={workflowUpdateDetails.isPending}>
            {t("common:continue")}
          </Button>
        </Stack>
      </form>
    </FullScreenPage>
  );
};
